import React, {PureComponent, ComponentType} from "react";
import {TweenLite, TweenMax, TimelineLite, Power4, Power2, Back, Animation} from "gsap";
import {AnimationSettingsProp} from "../../store/settings/types";
import {TileProps} from "./Tile";
import {HostElementRefProp} from "../../shared/components/HostElementRefProp";
import styles from "./level-map.module.css";
import levelMapStyles from "./level-map.module.css";
import topPanelStyles from "../../shared/components/top-panel/top-panel.module.css";
import icons from "../../shared/styles/icons.module.css";
import {stagger} from "../../shared/utils/stagger";
import {shakeScale} from "../../shared/utils/shake";
import {range} from "../../shared/utils/range";
import {random, randomInt} from "../../shared/utils/random";
import {spread} from "../../shared/utils/spread";
import playSound from "../../shared/sounds/playSound";
import { cssHelper } from "../../shared/utils/cssHelper";

interface AnimatedTileProps extends TileProps, AnimationSettingsProp {
    readonly allTilesAreRevealed: boolean;
    readonly scaleFactor: number;
}

export const animateTile = (Tile: ComponentType<HostElementRefProp & any>) => {
    const animate = stagger(0.15);

    return class extends PureComponent<AnimatedTileProps> {
        private readonly hostElement: React.RefObject<HTMLDivElement>;
        private readonly activeTweens: Animation[] = [];

        constructor(props: AnimatedTileProps, context: any) {
            super(props, context);

            this.hostElement = React.createRef();
        }

        componentDidMount(): void {
            if (!this.hostElement.current)
                return;

            const frontFace = this.hostElement.current.querySelector(`div:not(.${styles.tile_back})`);
            const backFace = this.hostElement.current.querySelector(`.${styles.tile_back}`);

            if (this.props.isRevealed) {
                if (frontFace) {
                    TweenLite.set(frontFace, {
                        rotationY: 0
                    });
                }

                if (backFace) {
                    TweenLite.set(backFace, {
                        rotationY: 180
                    });
                }
            }

            if (this.props.score != null) {
                const score = this.hostElement.current.parentElement!.getElementsByClassName(styles.tile_score);

                TweenLite.set(score, {
                    opacity: 0
                });
            }
        }

        componentDidUpdate(prevProps: Readonly<AnimatedTileProps>, prevState: Readonly<{}>, snapshot?: any): void {
            const {scaleFactor, allTilesAreRevealed, animation} = this.props;

            if (!this.hostElement.current || !animation.enabled)
                return;

            if (this.props.isRevealed && !prevProps.isRevealed) {
                const frontFace = this.hostElement.current.querySelector(`div:not(.${styles.tile_back})`) as HTMLDivElement;
                const backFace = this.hostElement.current.querySelector(`.${styles.tile_back}`) as HTMLDivElement;

                if (allTilesAreRevealed) {
                    if (frontFace) {
                        const delay = random(0.2);

                        const timeLine = new TimelineLite();
                        timeLine.timeScale(animation.timeScale);

                        timeLine.to(frontFace, 0.5, {
                            rotationY: 0,
                            delay
                        });

                        timeLine.to(backFace, 0.5, {
                            rotationY: 180,
                            delay
                        }, 0);

                        this.activeTweens.push(timeLine);
                    }
                } else {
                    if (frontFace) {
                        const tween = animate([{
                            element: frontFace, 
                            props: {
                                rotationY: 0,
                                onStart: () => playSound("level-item-flip")
                            }
                        },
                        {
                            element: backFace, 
                            props: {
                                rotationY: 180,
                                onStart: () => playSound("level-item-flip")
                            }
                        }], 0.5, animation.timeScale);

                        this.activeTweens.push(tween);
                    }
                }
            }

            if (this.props.score != null && typeof(prevProps.score) == 'undefined') {
                const icon = this.hostElement.current.parentElement!.getElementsByClassName(styles.icon);
                const score = this.hostElement.current.parentElement!.getElementsByClassName(styles.tile_score);
                const answer = this.hostElement.current.parentElement!.getElementsByClassName(styles.tile_answer);

                const timeLine = new TimelineLite();
                timeLine.timeScale(animation.timeScale);

                timeLine
                    .set(this.hostElement.current,{
                        willChange: "transform, opacity"
                    })
                    .set(icon,{
                        opacity: 1
                    })
                    .set(score,{
                        opacity: 0,
                        scale: 1,
                    })
                    .set(answer,{
                        opacity: 0
                    });

                timeLine
                    .add(shakeScale(this.hostElement.current, 0.3, 5, 0.6), "first")
                    .to(icon, 0.3, {
                        opacity: 0,
                        ease: Power4.easeOut,
                        onComplete: () => playSound("mouth-pop")
                    }, "first")

                    .to(score, 0.6, {
                        opacity: 1,
                        delay: 0.1,
                        ease: Power4.easeOut
                    }, "first")

                    .to(score, 0.6, {
                        opacity: 0,
                        scale: 1.5
                    }, "second")
                    .to(answer, 0.6, {
                        opacity: 1,
                        delay: 0.3,
                    }, "second");

                if (this.props.score !== 0) {
                    const levelMap = document.body.getElementsByClassName(levelMapStyles.scroll_view)[0] as HTMLElement;
                    const mapField = document.body.getElementsByClassName(levelMapStyles.field)[0] as HTMLElement;
                    const canvas = document.body.getElementsByClassName(levelMapStyles.canvas)[0] as HTMLElement;
                    const topPanel = document.body.getElementsByClassName(topPanelStyles.top_panel)[0] as HTMLElement;
                    const topPanelScore = topPanel.getElementsByClassName(topPanelStyles.score_icon)[0] as HTMLElement;
                    if (!levelMap || !mapField || !topPanel || !topPanelScore)
                        return;

                    const canvasOffset = parseFloat(canvas.style.top + "") || 0;

                    const tilePosition =  {
                        left: this.hostElement.current!.offsetLeft
                            + this.hostElement.current!.offsetWidth / 2
                            + (levelMap.offsetWidth - mapField.offsetWidth) / 2,

                        top: this.hostElement.current!.offsetTop
                            + canvasOffset
                            + this.hostElement.current!.offsetHeight / 2
                    };

                    const stars = this.spawnStars(levelMap, tilePosition, this.props.score!, this.props.additionalScore || 0);
                    if (!stars.length)
                        return;

                    timeLine.call(() => playSound("stars-spew"), undefined, undefined, "second");

                    timeLine.add(shakeScale(this.hostElement.current, 0.3, 4, 0.6, 0.3), "second");

                    const stepDelay = 0.6 / this.props.score!;
                    const initialDelay = -0.6;
                    const scoreBounds = topPanelScore.getBoundingClientRect();

                    for (let i = 0; i < stars.length; i++) {
                        const star = stars[i];
                        const radius = random(20, 60);
                        const angle = random(2 * Math.PI);
                        const x = Math.round(radius * Math.cos(angle));
                        const y = Math.round(radius * Math.sin(angle));

                        timeLine.to(star, 0.2, {
                            x,
                            y,
                            opacity: 1,
                            delay: 0.2,
                            ease: Power2.easeOut
                        }, "second");

                        const tileBounds = this.hostElement.current!.getBoundingClientRect();

                        timeLine.to(star, 0.8, {
                            x: (scoreBounds.left - tileBounds.left) / scaleFactor - (star.offsetWidth / 2),
                            y: (scoreBounds.top - tileBounds.top) / scaleFactor + (star.offsetHeight / 2),
                            scale: 1,
                            rotation: spread([-360, 360], 0.25),
                            delay: i * stepDelay + initialDelay,
                            ease: Back.easeIn.config(1),
                            onComplete: () => {
                                if (levelMap && star && star.parentElement === levelMap) {
                                    levelMap.removeChild(star);
                                }
                            }
                        }, "third");

                        timeLine.to(topPanelScore, 0.1, {
                            scaleX: spread([0.75, 1.25], 0.15),
                            scaleY: spread([0.75, 1.25], 0.15),
                            onStart: () => playSound("score-fly-in"),
                            delay: 0.8 + i * stepDelay + initialDelay,
                        }, "third");

                        timeLine.to(topPanelScore, 0.1, {
                            scaleX: 1,
                            scaleY: 1,
                            delay: 0.9 + i * stepDelay + initialDelay,
                        }, "third");
                    }
                }

                this.activeTweens.push(timeLine);
            }
        }

        componentWillUnmount(): void {
            for (let animation of this.activeTweens) {
                animation.kill();
            }
        }

        render() {
            if (!this.props.tileType || !this.props.animation.enabled) {
                return (
                    <Tile {...this.props} onClick={this.onClick.bind(this)}/>
                );
            }

            return (
                <div className={styles.tile_wrapper} onClick={this.onClick.bind(this)} ref={this.hostElement} data-animated={1}>
                    <div className={styles.tile_back}>
                        <i className={`${styles.icon} ${this.props.scenery}_unrevealed_tile`}/>
                    </div>

                    <Tile {...this.props} onClick={undefined}/>
                </div>
            );
        }

        private onClick() {
            const {isRevealed, isAnswered, animation} = this.props;

            if (this.hostElement.current && animation.enabled) {
                const target = !isRevealed || isAnswered
                    ? this.hostElement.current.getElementsByClassName(styles.tile_back)
                    : this.hostElement.current.getElementsByClassName(styles.tile);

                const tween = TweenMax.fromTo(target, 0.1, {
                    opacity: 1,
                    scale: 1
                }, {
                    ...(!isRevealed || isAnswered
                        ? {
                            opacity: 0.8,
                        }
                        : {
                            scale: 1.1,
                        }),
                    repeat: 1,
                    yoyo: true,
                    onComplete: () => {
                        if (this.props.onClick) {
                            this.props.onClick();
                        }
                    }
                } as any).timeScale(animation.timeScale);

                this.activeTweens.push(tween);
            } else {
                this.props.onClick && this.props.onClick();
            }

            if (this.props.tileType) {
                if (this.props.isRevealed && !this.props.isAnswered) {
                    playSound("question-select");
                } else {
                    playSound("unavailable-level-item-click");
                }
            }
        }

        private spawnStars(levelMap: HTMLElement, position: { left: number, top: number }, count: number, additional: number) {
            return range(count + additional).map((i: number) => {
                const star = document.createElement('div');
                star.classList.add(styles.score_star);
                star.classList.add(i >= count ? icons.score_star2 : icons.score_star1);

                star.style.left = (position.left) + 'px';
                star.style.top = position.top + 'px';

                TweenLite.set(star, {
                    opacity: 0,
                    zIndex: 40,
                    scale: random(0.7, 1.3),
                    rotation: randomInt(-10, 10),
                    willChange: "transform, opacity"
                });
                levelMap.appendChild(star);

                return star;
            });
        }
    };
};